import { useEffect, useState, useMemo } from "react"
import { Button, Col, Row, Space, Table, Typography, Select, Statistic } from "antd"
import {
  CloseCircleOutlined,
  FileOutlined,
  HistoryOutlined,
  SearchOutlined,
  DollarCircleOutlined,
  ProfileOutlined,
} from "@ant-design/icons"
import moment from "moment"
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore"

import { db } from "../../../firebase/config"
import PageHeader from "../../../components/Elements/PageHeader"
import ColumnSearchFilter from "../../../components/Table/orderNumberFilter"
import { OrderAndPrefix } from "../../../components/Orders/Helpers/OrderAndPrefix"
import { orderEndDate, orderStartDate } from "../../../components/Table/orderDateDisplay"
import OrderCountDisplay from "../../../components/Table/orderCountDisplay"

const { Text, Title } = Typography
const { Option } = Select

export default function MpOrders() {
  // 1) Firestore data
  const [allFetchedData, setAllFetchedData] = useState([])
  const [loading, setLoading] = useState(false)

  // 2) Month/Year selects
  //    month: integer 0..11 or null
  //    year: integer (e.g. 2024) or null
  const [selectedMonth, setSelectedMonth] = useState(null)
  const [selectedYear, setSelectedYear] = useState(null)

  // 3) Fetch orders on mount
  useEffect(() => {
    setLoading(true)
    const ordersRef = collection(db, "orders")

    const activeQ = query(
      ordersRef,
      where("uid", "==", "C6eD2XCyx5Vi7ANQwqlwdb0Q9Hf2"),
      where("orderStatus", "==", 7),
      orderBy("completedTime", "desc")
    )

    const unsubscribe = onSnapshot(
      activeQ,
      (snapshot) => {
        const allResults = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        // Filter out by manufacturer.id
        const manufacturerFiltered = allResults.filter(
          (order) => order.manufacturer?.id === "Pg12Q17YcAdwoewHilbq"
        )
        setAllFetchedData(manufacturerFiltered)
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching orders:", error)
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  // 4) Derive year options from completedTime in allFetchedData
  const yearOptions = useMemo(() => {
    const years = allFetchedData
      .map((order) => {
        if (order.completedTime?.seconds) {
          return moment.unix(order.completedTime.seconds).year()
        }
        return null
      })
      .filter((y) => y !== null)
    const uniqueYears = [...new Set(years)].sort((a, b) => b - a) // newest first
    return uniqueYears
  }, [allFetchedData])

  // 5) Process data: filter by selectedMonth & selectedYear, then compute orderTotalPrice
  const processedData = useMemo(() => {
    if (!allFetchedData) return []

    // First, optionally filter by month+year
    let dataToUse = allFetchedData
    if (selectedYear !== null) {
      dataToUse = dataToUse.filter((order) => {
        if (!order.completedTime?.seconds) return false
        const m = moment.unix(order.completedTime.seconds)
        if (selectedMonth !== null) {
          return m.year() === selectedYear && m.month() === selectedMonth
        }
        // If only year is selected, ignore month
        return m.year() === selectedYear
      })
    } else if (selectedMonth !== null) {
      // If year is null but month is chosen, filter any year matching month
      dataToUse = dataToUse.filter((order) => {
        if (!order.completedTime?.seconds) return false
        const m = moment.unix(order.completedTime.seconds)
        return m.month() === selectedMonth
      })
    }

    // Next, compute orderTotalPrice (per‐roll logic: amountCreated/1000 * price)
    return dataToUse.map((order) => {
      const price = parseFloat(order.price) || 0
      const amountCreated = parseInt(order.amountCreated, 10) || 0
      const orderTotal = (amountCreated / 1000) * price
      return {
        ...order,
        orderTotalPrice: isNaN(orderTotal) ? 0 : orderTotal,
      }
    })
  }, [allFetchedData, selectedMonth, selectedYear])

  // 6) Push processedData into filteredData for Table + summary
  const [filteredData, setFilteredData] = useState([])
  useEffect(() => {
    setFilteredData(processedData)
  }, [processedData])

  // 7) Monthly summary
  const monthSummary = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { totalOrders: 0, totalRevenue: 0 }
    }
    const totalRevenue = filteredData.reduce((sum, order) => sum + (order.orderTotalPrice || 0), 0)
    return {
      totalOrders: filteredData.length,
      totalRevenue,
    }
  }, [filteredData])

  // 8) Client & Product filters (based on filteredData)
  const clientFilters = useMemo(() => {
    if (!filteredData) return []
    const uniqueClients = [...new Set(filteredData.map((o) => o.client?.client).filter(Boolean))]
    return uniqueClients.map((c) => ({ text: c, value: c }))
  }, [filteredData])

  const productFilters = useMemo(() => {
    if (!filteredData) return []
    const uniqueProducts = [
      ...new Set(filteredData.map((o) => o.selectedProduct?.product?.productName).filter(Boolean)),
    ]
    return uniqueProducts.map((p) => ({ text: p, value: p }))
  }, [filteredData])

  // 9) Show invoice button
  const returnCorrectButton = (record) => {
    if (record.hasInvoice) {
      return (
        <Space>
          <a href={record.invoice?.invoiceUrl} target="_blank" rel="noopener noreferrer">
            <Button size="small" icon={<FileOutlined style={{ color: "#1677ff" }} />}>
              Show
            </Button>
          </a>
        </Space>
      )
    } else {
      return (
        <Space>
          <Text type="secondary">No invoice</Text>
          <CloseCircleOutlined style={{ color: "#f5222d" }} />
        </Space>
      )
    }
  }

  // 10) Table columns
  const defaultColumns = [
    {
      title: "Nr.",
      dataIndex: "orderNumber",
      align: "center",
      sorter: (a, b) => (a.justOrderNumber || 0) - (b.justOrderNumber || 0),
      render: (value, record) => OrderAndPrefix(record),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <ColumnSearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
          dataIndex="orderNumber"
        />
      ),
      onFilter: (value, record) =>
        record.orderNumber?.toString().trim().toLowerCase().includes(value.trim().toLowerCase()),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "center",
      filters: clientFilters,
      onFilter: (value, record) => record.client?.client?.startsWith(value),
      filterSearch: true,
    },
    {
      title: "Product",
      dataIndex: ["selectedProduct", "product", "productName"],
      align: "center",
      filters: productFilters,
      onFilter: (value, record) => record.selectedProduct?.product?.productName === value,
      filterSearch: true,
      width: "25%",
      render: (value) =>
        value ? <Text copyable={{ text: value }}>{value}</Text> : <Text type="secondary">N/A</Text>,
    },
    {
      title: "Count (Labels)",
      dataIndex: "amountCreated",
      align: "center",
      render: (value, record) => <OrderCountDisplay value={value} record={record} />,
    },
    {
      title: "Order Total (€)",
      dataIndex: "orderTotalPrice",
      align: "center",
      sorter: (a, b) => (a.orderTotalPrice || 0) - (b.orderTotalPrice || 0),
      render: (value) =>
        value !== undefined ? `€${value.toFixed(2)}` : <Text type="secondary">N/A</Text>,
    },
    {
      title: "Invoice",
      align: "center",
      render: (value, record) => returnCorrectButton(record),
    },
    {
      title: "Started",
      dataIndex: "created",
      align: "center",
      key: "created",
      sorter: (a, b) => (a.created?.seconds || 0) - (b.created?.seconds || 0),
      render: (value, record) => orderStartDate(value, record),
    },
    {
      title: "Completed",
      dataIndex: "completedTime",
      align: "center",
      key: "completedTime",
      sorter: (a, b) => (a.completedTime?.seconds || 0) - (b.completedTime?.seconds || 0),
      render: (value, record) => orderEndDate(value, record),
    },
  ]

  // 11) Month names static array for dropdown
  const monthNames = moment.months() // ["January", "February", ... ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<HistoryOutlined />} text="MP Orders History" />
        </Col>

        {/* ─── Month + Year Selects ─── */}
        <Col span={24}>
          <Space align="center" wrap style={{ marginBottom: 20 }}>
            <Select
              placeholder="Select Month"
              value={selectedMonth}
              onChange={(m) => setSelectedMonth(m)}
              style={{ width: 140 }}
              allowClear>
              {monthNames.map((name, idx) => (
                <Option key={idx} value={idx}>
                  {name}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Select Year"
              value={selectedYear}
              onChange={(y) => setSelectedYear(y)}
              style={{ width: 120 }}
              allowClear>
              {yearOptions.map((yr) => (
                <Option key={yr} value={yr}>
                  {yr}
                </Option>
              ))}
            </Select>

            {/* Only show a Clear‐All button if either is selected */}
            {(selectedMonth !== null || selectedYear !== null) && (
              <Button
                onClick={() => {
                  setSelectedMonth(null)
                  setSelectedYear(null)
                }}>
                Clear Filters
              </Button>
            )}
          </Space>
        </Col>

        {/* ─── Summary ─── */}
        <Col span={24} style={{ marginBottom: 20 }}>
          <Title level={4}>
            Summary for{" "}
            {selectedMonth !== null || selectedYear !== null
              ? `${selectedMonth !== null ? monthNames[selectedMonth] : "All Months"} ${
                  selectedYear !== null ? selectedYear : ""
                }`
              : "All Displayed Orders"}
          </Title>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Total Orders"
                value={monthSummary.totalOrders}
                prefix={<ProfileOutlined />}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Statistic
                title="Total Revenue"
                value={monthSummary.totalRevenue}
                prefix="€"
                precision={2}
                valueStyle={{ color: "#3f8600" }}
                suffix={<DollarCircleOutlined />}
              />
            </Col>
          </Row>
        </Col>

        {/* ─── The Table ─── */}
        <Col span={24}>
          <Table
            style={{ marginTop: "15px" }}
            size="small"
            loading={loading}
            columns={defaultColumns}
            bordered
            dataSource={filteredData}
            rowKey={(record) => record.id}
            pagination={{
              defaultPageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: ["10", "25", "50", "100"],
            }}
            scroll={{ x: 1200 }}
          />
        </Col>
      </Row>
    </div>
  )
}
